import { useMemo, useState } from 'react';
import { XMarkIcon, InformationCircleIcon, ExclamationTriangleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useActiveAnnouncements } from '@/hooks/useApi';
import { cn } from '@/lib/cn';

const DISMISSED_KEY = 'ledgora.dismissedAnnouncements';

function getDismissedIds(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function dismissId(id: string) {
  const ids = getDismissedIds();
  if (!ids.includes(id)) {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids, id]));
  }
}

const SEVERITY_STYLES = {
  info: { wrapper: 'bg-sky-50 border-sky-200 text-sky-800', icon: InformationCircleIcon, iconColor: 'text-sky-500' },
  warning: { wrapper: 'bg-amber-50 border-amber-200 text-amber-800', icon: ExclamationTriangleIcon, iconColor: 'text-amber-500' },
  critical: { wrapper: 'bg-rose-50 border-rose-200 text-rose-800', icon: ExclamationCircleIcon, iconColor: 'text-rose-500' },
} as const;

export default function AnnouncementBanner() {
  const { data: announcements } = useActiveAnnouncements();
  const [, forceUpdate] = useState(0);

  const visible = useMemo(() => {
    const dismissed = getDismissedIds();
    return (announcements ?? []).filter((a) => !dismissed.includes(a.id));
  }, [announcements]);

  if (visible.length === 0) return null;

  return (
    <div className="mb-6 space-y-2">
      {visible.map((a) => {
        const style = SEVERITY_STYLES[a.severity] ?? SEVERITY_STYLES.info;
        const Icon = style.icon;
        return (
          <div key={a.id} className={cn('flex items-start gap-3 rounded-xl border px-4 py-3 shadow-sm', style.wrapper)}>
            <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', style.iconColor)} />
            <div className="flex-1 text-sm">
              <div className="font-semibold">{a.title}</div>
              <div className="mt-0.5">{a.message}</div>
            </div>
            <button
              type="button"
              onClick={() => {
                dismissId(a.id);
                forceUpdate((n) => n + 1);
              }}
              className="flex-shrink-0 rounded-md p-1 text-current opacity-60 hover:opacity-100 transition-opacity"
              aria-label="Dismiss"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
